import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Loader2, Download, AlertTriangle, UserCheck, ShieldCheck, Users } from "lucide-react";
import { toast } from "sonner";
import BrandedHeader from "@/components/BrandedHeader";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUS_VARIANTS = {
  completed: "bg-green-100 text-green-800",
  invited: "bg-blue-100 text-blue-800",
  started: "bg-amber-100 text-amber-800",
  pending: "bg-gray-100 text-gray-700",
};

/**
 * Hiring-manager self-service portal.
 * URL pattern: /hiring-manager/auth/:token → activates magic link,
 * then shows scoped candidate list + CSV export with PII toggle.
 */
export default function HiringManagerPortalPage() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [session, setSession] = useState(null); // { hiring_manager, job, access_token }
  const [candidates, setCandidates] = useState([]);
  const [redactPii, setRedactPii] = useState(false);

  const activate = useCallback(async () => {
    try {
      const res = await axios.post(`${BACKEND}/api/hiring-manager/auth/${encodeURIComponent(token)}`);
      setSession(res.data);
      const listRes = await axios.get(`${BACKEND}/api/hiring-manager/candidates`, {
        headers: { Authorization: `Bearer ${res.data.access_token}` },
      });
      setCandidates(listRes.data.candidates || []);
    } catch (e) {
      const detail = e.response?.data?.detail || "Invalid or expired access link";
      setError(typeof detail === "string" ? detail : "Invalid access link");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    activate();
  }, [activate]);

  const handleDownload = () => {
    if (!session) return;
    const url =
      `${BACKEND}/api/hiring-manager/candidates/export-csv` +
      (redactPii ? "?redact_pii=true" : "");
    // Open with Authorization via a fetch-then-blob to preserve bearer token.
    (async () => {
      try {
        const res = await fetch(url, { headers: { Authorization: `Bearer ${session.access_token}` } });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        const rows = res.headers.get("X-Total-Rows") || candidates.length;
        const cd = res.headers.get("Content-Disposition") || "";
        const filenameMatch = cd.match(/filename=([^;]+)/);
        const filename = filenameMatch ? filenameMatch[1] : `hm_${session.job.id.slice(0, 8)}.csv`;
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = filename;
        a.click();
        URL.revokeObjectURL(a.href);
        toast.success(`Downloaded ${rows} candidate row${rows === "1" ? "" : "s"}${redactPii ? " (PII redacted)" : ""}`);
      } catch (err) {
        toast.error("Download failed — try re-opening the link from your email");
      }
    })();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-primary animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4" data-testid="hm-error-screen">
        <Card className="max-w-md w-full border-border bg-card">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="w-12 h-12 text-[hsl(var(--warning))] mx-auto mb-4" />
            <h2 className="text-xl font-display font-semibold mb-2">Access denied</h2>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button variant="outline" onClick={() => navigate("/")}>Back to home</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="hm-portal-page">
      <BrandedHeader className="flex-shrink-0">
        <div className="flex items-center gap-3 flex-1">
          <UserCheck className="w-5 h-5 text-primary" />
          <div>
            <span className="font-display font-semibold text-sm">Hiring Manager Portal</span>
            <span className="text-xs text-muted-foreground ml-2">{session?.hiring_manager?.email}</span>
          </div>
        </div>
      </BrandedHeader>

      <div className="flex-1 px-4 sm:px-6 py-8 overflow-auto">
        <div className="max-w-4xl mx-auto space-y-6">
          <Card className="border-border bg-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />
                {session?.job?.title}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 rounded-lg bg-secondary/40">
                  <div className="text-3xl font-display font-semibold">{candidates.length}</div>
                  <div className="text-xs text-muted-foreground mt-1">Total candidates</div>
                </div>
                <div className="p-4 rounded-lg bg-secondary/40">
                  <div className="text-3xl font-display font-semibold">
                    {candidates.filter((c) => c.status === "completed").length}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">Completed</div>
                </div>
                <div className="p-4 rounded-lg bg-secondary/40">
                  <div className="text-3xl font-display font-semibold">
                    {candidates.filter((c) => c.workflow_status === "shortlisted").length}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">Shortlisted</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border bg-card" data-testid="hm-export-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Download className="w-5 h-5 text-primary" />
                Export candidate list
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between p-3 rounded-lg border mb-4">
                <div className="flex items-center gap-3">
                  <ShieldCheck className={`w-4 h-4 ${redactPii ? "text-[hsl(var(--success))]" : "text-muted-foreground"}`} />
                  <div>
                    <Label className="text-sm font-medium">Redact PII in export</Label>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Mask email (j***@example.com), phone (***1234), and truncate CV summary to 40 chars.
                    </p>
                  </div>
                </div>
                <Switch checked={redactPii} onCheckedChange={setRedactPii} data-testid="hm-redact-pii-switch" />
              </div>

              <Button onClick={handleDownload} className="w-full" data-testid="hm-download-btn" disabled={candidates.length === 0}>
                <Download className="w-4 h-4 mr-2" />
                Download CSV {redactPii && <Badge variant="secondary" className="ml-2 text-[10px]">PII redacted</Badge>}
              </Button>
              {candidates.length === 0 && (
                <p className="text-xs text-muted-foreground text-center mt-3">
                  No candidates yet — the list will populate as they apply.
                </p>
              )}
            </CardContent>
          </Card>

          {candidates.length > 0 && (
            <Card className="border-border bg-card">
              <CardHeader>
                <CardTitle className="text-base">Preview (first 25 rows, always PII-redacted)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-xs text-muted-foreground border-b">
                      <tr>
                        <th className="text-left py-2 font-medium">Name</th>
                        <th className="text-left py-2 font-medium">Status</th>
                        <th className="text-left py-2 font-medium">Score</th>
                        <th className="text-left py-2 font-medium">Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {candidates.slice(0, 25).map((c) => (
                        <tr key={c.id} className="border-b last:border-0" data-testid={`hm-candidate-row-${c.id}`}>
                          <td className="py-2">{c.name}</td>
                          <td className="py-2">
                            <Badge className={`${STATUS_VARIANTS[c.status] || "bg-gray-100"} text-xs`}>
                              {c.status || "unknown"}
                            </Badge>
                          </td>
                          <td className="py-2">{c.score_pct ?? c.test_score ?? "—"}</td>
                          <td className="py-2 text-xs text-muted-foreground">
                            {c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
